import React from 'react';
import { ListTree } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { SearchableOption } from '@/components/shared/SearchableSelect';
import RowAccountPickerDialog, { type RowPickerDraft } from '@/components/document-entry/RowAccountPickerDialog';
import { accountCodeForHeadingLookup, parseAccountCodeProject } from '@/lib/accountingAccountProjectCode';
import { findHeadingByCode, headingLabel, type HeadingFlat } from '@/document-entry/headingCatalog';

type Props = {
  accountCode: string;
  onAccountCodeChange: (code: string) => void;
  headingCatalog: HeadingFlat[];
  disabled?: boolean;
  /** فقط انتخاب حساب (کل/معین/تفصیلی) — بدون پروژه و طرف */
  mode?: 'full' | 'accountOnly';
  projectId?: string;
  beneficiaryKey?: string;
  onProjectIdChange?: (id: string) => void;
  onBeneficiaryKeyChange?: (key: string) => void;
  projectOptions?: SearchableOption[];
  beneficiaryOptions?: SearchableOption[];
  loadingHeadingCatalog?: boolean;
  loadingProjects?: boolean;
};

function emptyDraft(): RowPickerDraft {
  return {
    accountCode: '',
    projectId: '',
    beneficiaryKey: '',
    filterGeneralCode: '',
    filterSubsidiaryCode: '',
  };
}

const RowAccountPickerField: React.FC<Props> = ({
  accountCode,
  onAccountCodeChange,
  headingCatalog,
  disabled,
  mode = 'full',
  projectId = '',
  beneficiaryKey = '',
  onProjectIdChange,
  onBeneficiaryKeyChange,
  projectOptions = [],
  beneficiaryOptions = [],
  loadingHeadingCatalog = false,
  loadingProjects = false,
}) => {
  const accountOnly = mode === 'accountOnly';
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<RowPickerDraft>(emptyDraft);
  const [pickerSearch, setPickerSearch] = React.useState('');

  const openPicker = React.useCallback(
    (searchSeed = '', clearSearch = false) => {
      const parsed = parseAccountCodeProject(accountCode);
      const baseCode = parsed.hasProjectSuffix ? parsed.baseAccountCode : accountCode.trim();
      const lookup = accountCodeForHeadingLookup(baseCode);
      const hit = findHeadingByCode(headingCatalog, baseCode) ?? findHeadingByCode(headingCatalog, lookup);
      setDraft({
        accountCode: baseCode,
        projectId: parsed.projectId ? String(parsed.projectId) : projectId,
        beneficiaryKey,
        filterGeneralCode: hit?.level === 'subsidiary' || hit?.level === 'detail' ? hit.parentCode : '',
        filterSubsidiaryCode: hit?.level === 'detail' ? hit.parentCode : '',
      });
      setPickerSearch(clearSearch ? '' : searchSeed || baseCode);
      setOpen(true);
    },
    [accountCode, beneficiaryKey, headingCatalog, projectId],
  );

  const applyTypedCode = React.useCallback(
    (raw: string) => {
      const code = raw.trim();
      onAccountCodeChange(code);
      if (headingCatalog.length === 0) return;
      const lookup = accountCodeForHeadingLookup(code);
      const exact = findHeadingByCode(headingCatalog, code) ?? findHeadingByCode(headingCatalog, lookup);
      if (exact) return;
      if (code) openPicker(code);
    },
    [headingCatalog, onAccountCodeChange, openPicker],
  );

  const confirmPicker = React.useCallback(() => {
    const baseCode = draft.accountCode.trim();
    onAccountCodeChange(baseCode);
    if (accountOnly) {
      onProjectIdChange?.(draft.projectId);
    } else {
      onProjectIdChange?.(draft.projectId);
      onBeneficiaryKeyChange?.(draft.beneficiaryKey);
    }
    setOpen(false);
  }, [accountOnly, draft, onAccountCodeChange, onBeneficiaryKeyChange, onProjectIdChange]);

  const lookupCode = accountCodeForHeadingLookup(accountCode);
  const hint = headingLabel(headingCatalog, lookupCode);
  const parsedProject = parseAccountCodeProject(accountCode);
  const activeProjectId =
    projectId ||
    (parsedProject.hasProjectSuffix && parsedProject.projectId ? String(parsedProject.projectId) : '');
  const projectHint = activeProjectId
    ? projectOptions.find((p) => p.value === activeProjectId)?.label ?? `پروژه #${activeProjectId}`
    : null;

  return (
    <div className="min-w-[14rem] space-y-1">
      <div className="flex gap-1">
        <Input
          dir="ltr"
          className="font-mono text-xs"
          value={accountCode}
          disabled={disabled}
          placeholder="کد حساب — Enter برای انتخاب"
          title={hint || undefined}
          onChange={(e) => onAccountCodeChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyTypedCode(accountCode);
            }
          }}
          onBlur={() => {
            const code = accountCode.trim();
            if (!code) return;
            if (headingCatalog.length === 0) return;
            const lookup = accountCodeForHeadingLookup(code);
            if (!findHeadingByCode(headingCatalog, code) && !findHeadingByCode(headingCatalog, lookup)) {
              openPicker(code);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          title="انتخاب با تب‌بندی"
          onClick={() => openPicker('', true)}
        >
          <ListTree className="h-4 w-4" />
        </Button>
      </div>
      {hint && hint !== lookupCode ? (
        <p className="truncate text-[10px] text-slate-500" title={hint}>
          {hint}
        </p>
      ) : null}
      {projectHint ? (
        <p className="truncate text-[10px] font-medium text-sky-700" title={projectHint}>
          پروژه: {projectHint}
        </p>
      ) : null}

      <RowAccountPickerDialog
        open={open}
        onOpenChange={setOpen}
        draft={draft}
        onDraftChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
        onConfirm={confirmPicker}
        headingCatalog={headingCatalog}
        projectOptions={projectOptions}
        beneficiaryOptions={beneficiaryOptions}
        initialSearch={pickerSearch}
        accountOnly={accountOnly}
        loadingHeadingCatalog={loadingHeadingCatalog}
        loadingProjects={loadingProjects}
      />
    </div>
  );
};

export default RowAccountPickerField;
